import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  PERMISSIONS,
  dutyTotals,
  minutesBetween,
  type CreateDutyLogInput,
  type DutyLog,
  type DutySuggestion,
} from '@jupi/shared'
import { api, ApiError, ConfirmDialog, formatDate, IconButton, RemoteContent, TimeField, Title, useAuth, useClubToday, useSnackbar } from '@jupi/ui'

// An ATO instructor's or student's duty log (fsm's DutyLogsPage), with
// the duty-period limits as of today and a warning before an entry would
// break one. Entering a date fills the duty period from that day's ATO
// training flights.

const hm = (minutes: number) => `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`
const day = (iso: string) => formatDate(`${iso}T12:00:00Z`)

export default function DutyLogPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const today = useClubToday()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [toDelete, setToDelete] = useState<DutyLog | null>(null)

  const { data: logs = [], isLoading, error } = useQuery({
    queryKey: ['school', 'dutyLogs'],
    queryFn: () => api.get<{ dutyLogs: DutyLog[] }>('/api/school/duty-logs').then((r) => r.dutyLogs),
  })
  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/api/school/duty-logs/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['school', 'dutyLogs'] }),
    onError: () => showSnackbar(t('common.error'), { severity: 'error' }),
  })
  const keepsLog = can(PERMISSIONS.fsmAtoInstruct, PERMISSIONS.fsmAtoStudent)

  return (
    <Box>
      <Title label={t('nav.dutyLog')}>
        {keepsLog ? (
          <Button variant="contained" startIcon={<Icon icon="mdi:plus" />} onClick={() => setAdding(true)}>
            {t('school.addDuty')}
          </Button>
        ) : null}
      </Title>
      <RemoteContent isLoading={isLoading} error={error}>
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('school.dutyLimitsToday')}
              </Typography>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
                {dutyTotals(logs, today).map((w) => (
                  <Box key={w.window}>
                    <Typography variant="caption" color="text.secondary">
                      {t(`school.dutyWindow_${w.window}`)}
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }} color={w.exceeded ? 'error' : undefined}>
                      {hm(w.totalMinutes)} / {w.limitMinutes / 60} h
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      color={w.exceeded ? 'error' : w.totalMinutes > w.limitMinutes * 0.8 ? 'warning' : 'primary'}
                      value={Math.min(100, (w.totalMinutes / w.limitMinutes) * 100)}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          {logs.length === 0 && !isLoading ? <Typography color="text.secondary">{t('school.noDuty')}</Typography> : null}
          {logs.map((log) => (
            <Card key={log.dutyLogId} variant="outlined">
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    {day(log.logDate)}
                    {log.dutyStart && log.dutyEnd ? ` · ${log.dutyStart}–${log.dutyEnd}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[
                      log.dutyMinutes !== null ? `${t('school.duty')} ${hm(log.dutyMinutes)}` : null,
                      log.flightMinutes ? `${t('school.flight')} ${hm(log.flightMinutes)}` : null,
                      log.registration,
                      [log.departure, log.destination].filter(Boolean).join('–') || null,
                      log.remarks,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
                <IconButton onClick={() => setToDelete(log)} aria-label={t('common.delete')}>
                  <Icon icon="mdi:delete-outline" />
                </IconButton>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </RemoteContent>

      {adding ? <AddDutyDialog logs={logs} onClose={() => setAdding(false)} /> : null}
      <ConfirmDialog
        open={toDelete !== null}
        title={t('school.deleteDuty')}
        message={toDelete ? day(toDelete.logDate) : ''}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.close')}
        onConfirm={() => {
          if (toDelete) remove.mutate(toDelete.dutyLogId)
          setToDelete(null)
        }}
        onClose={() => setToDelete(null)}
      />
    </Box>
  )
}

function AddDutyDialog({ logs, onClose }: { logs: DutyLog[]; onClose: () => void }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const today = useClubToday()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    logDate: today,
    dutyStart: '',
    dutyEnd: '',
    flightMinutes: '',
    registration: '',
    departure: '',
    destination: '',
    remarks: '',
  })
  const set = (key: keyof typeof form, value: string) => setForm((f) => ({ ...f, [key]: value }))

  // Fill the duty period from the day's ATO flights, once per date and
  // only into empty fields -- never over what was typed.
  const { data: suggestion } = useQuery({
    queryKey: ['school', 'dutySuggestion', form.logDate],
    queryFn: () =>
      api.get<{ suggestion: DutySuggestion | null }>(`/api/school/duty-logs/suggestion?date=${form.logDate}`).then((r) => r.suggestion),
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(form.logDate),
  })
  useEffect(() => {
    if (!suggestion) return
    setForm((f) => ({
      ...f,
      dutyStart: f.dutyStart || suggestion.dutyStart,
      dutyEnd: f.dutyEnd || suggestion.dutyEnd,
      flightMinutes: f.flightMinutes || String(suggestion.blockMinutes),
    }))
  }, [suggestion])

  const dutyMinutes = form.dutyStart && form.dutyEnd ? minutesBetween(form.dutyStart, form.dutyEnd) : null
  const exceeded = dutyMinutes !== null ? dutyTotals(logs, form.logDate, dutyMinutes).filter((w) => w.exceeded) : []

  const save = useMutation({
    mutationFn: (input: CreateDutyLogInput) => api.post('/api/school/duty-logs', input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['school', 'dutyLogs'] })
      showSnackbar(t('admin.saved'))
      onClose()
    },
    onError: (err) => showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' }),
  })

  const submit = () =>
    save.mutate({
      logDate: form.logDate,
      dutyStart: form.dutyStart || null,
      dutyEnd: form.dutyEnd || null,
      flightMinutes: form.flightMinutes ? Number(form.flightMinutes) : null,
      registration: form.registration || null,
      departure: form.departure.toUpperCase() || null,
      destination: form.destination.toUpperCase() || null,
      remarks: form.remarks || null,
    })

  const time = (key: 'dutyStart' | 'dutyEnd', label: string) => (
    <TimeField fullWidth label={label} value={form[key]} onChange={(v) => set(key, v)} />
  )

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>{t('school.addDuty')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            type="date"
            label={t('school.date')}
            value={form.logDate}
            onChange={(e) => set('logDate', e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {suggestion ? (
            <Alert severity="info">
              {t('school.dutyFromFlights', { count: suggestion.flights.length })}:{' '}
              {suggestion.flights.map((f) => `${f.offBlockTime ?? '?'}–${f.onBlockTime ?? '?'} ${f.withName ?? ''}`.trim()).join(', ')}
            </Alert>
          ) : null}
          <Stack direction="row" spacing={2}>
            {time('dutyStart', t('school.dutyStart'))}
            {time('dutyEnd', t('school.dutyEnd'))}
          </Stack>
          {dutyMinutes !== null ? (
            <Typography variant="body2">
              {t('school.duty')}: <b>{hm(dutyMinutes)}</b>
            </Typography>
          ) : null}
          {exceeded.length > 0 ? (
            <Alert severity="warning">
              {t('school.dutyExceeded')}:{' '}
              {exceeded.map((w) => `${t(`school.dutyWindow_${w.window}`)} ${hm(w.totalMinutes)} / ${w.limitMinutes / 60} h`).join(', ')}
            </Alert>
          ) : null}
          <TextField
            label={t('school.flightMinutes')}
            value={form.flightMinutes}
            onChange={(e) => set('flightMinutes', e.target.value.replace(/\D/g, ''))}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          />
          <Stack direction="row" spacing={2}>
            <TextField fullWidth label={t('booking.aircraft')} value={form.registration} onChange={(e) => set('registration', e.target.value.toUpperCase())} />
            <TextField fullWidth label={t('school.departure')} value={form.departure} onChange={(e) => set('departure', e.target.value)} />
            <TextField fullWidth label={t('school.destination')} value={form.destination} onChange={(e) => set('destination', e.target.value)} />
          </Stack>
          <TextField multiline minRows={2} label={t('school.remarks')} value={form.remarks} onChange={(e) => set('remarks', e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!form.logDate || dutyMinutes === null || save.isPending} onClick={submit}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
